import type {
  FeaturedBadgeRule,
  FeaturedBadgeRuleCreatePayload,
  FeaturedBadgeRuleEntryValues,
  FeaturedBadgeRuleFormValues,
  FeaturedBadgeRuleGroup,
  FeaturedBadgeRuleGroupDeletePayload,
  FeaturedBadgeRuleGroupTogglePayload,
  FeaturedBadgeRuleUpdatePayload,
  FeaturedBadgeScope,
} from "../../../models/rulesFeaturedBadge";
import { FEATURED_BADGE_STATES } from "../../../models/rulesFeaturedBadge";
import {
  ALL_RULES_TENANTS_FILTER_ID,
  GRUPS_RULES_TENANT_ID,
  resolveRulesTenantDisplayName,
} from "../constants/rulesTenant";

const MINUTES_PER_HOUR = 60;
const MINUTES_PER_DAY = 24 * MINUTES_PER_HOUR;

export const FEATURED_BADGE_LABEL_MAX_LENGTH = 20;

type FeaturedBadgeScopeDimension = keyof FeaturedBadgeScope;

const FEATURED_BADGE_SCOPE_DIMENSIONS: FeaturedBadgeScopeDimension[] = [
  "tenantId",
  "eventTypeId",
  "eventSubTypeId",
];

const normalizeScopeValue = <T,>(value: T | null | undefined): T | null => value ?? null;

export const matchesFeaturedBadgeWildcardScope = (
  ruleScope: FeaturedBadgeScope,
  targetScope: FeaturedBadgeScope
): boolean =>
  FEATURED_BADGE_SCOPE_DIMENSIONS.every((dimension) => {
    const ruleValue = normalizeScopeValue(ruleScope[dimension]);
    if (ruleValue === null) {
      return true;
    }
    return ruleValue === normalizeScopeValue(targetScope[dimension]);
  });

export const isSameFeaturedBadgeScope = (
  left: FeaturedBadgeScope,
  right: FeaturedBadgeScope
): boolean =>
  FEATURED_BADGE_SCOPE_DIMENSIONS.every(
    (dimension) =>
      normalizeScopeValue(left[dimension]) === normalizeScopeValue(right[dimension])
  );

export interface ThresholdParts {
  days: number;
  hours: number;
  minutes: number;
}

export const splitThresholdMinutes = (totalMinutes: number): ThresholdParts => {
  const safe = Math.max(0, Math.trunc(Number.isFinite(totalMinutes) ? totalMinutes : 0));
  const days = Math.floor(safe / MINUTES_PER_DAY);
  const remainder = safe % MINUTES_PER_DAY;
  const hours = Math.floor(remainder / MINUTES_PER_HOUR);
  const minutes = remainder % MINUTES_PER_HOUR;

  return { days, hours, minutes };
};

export const combineThresholdMinutes = (parts: Partial<ThresholdParts>): number => {
  const days = Math.max(0, Math.trunc(parts.days ?? 0));
  const hours = Math.max(0, Math.min(23, Math.trunc(parts.hours ?? 0)));
  const minutes = Math.max(0, Math.min(59, Math.trunc(parts.minutes ?? 0)));

  return days * MINUTES_PER_DAY + hours * MINUTES_PER_HOUR + minutes;
};

export const formatThresholdDuration = (totalMinutes: number): string => {
  const { days, hours, minutes } = splitThresholdMinutes(totalMinutes);
  const parts: string[] = [];

  if (days > 0) {
    parts.push(`${days}d`);
  }
  if (hours > 0) {
    parts.push(`${hours}h`);
  }
  if (minutes > 0 || parts.length === 0) {
    parts.push(`${minutes}m`);
  }

  return parts.join(" ");
};

export const FEATURED_BADGE_ALL_TENANTS_LABEL = "Todos los tenants (General)";

export const toApiFeaturedBadgeTenantId = (
  formTenantId: string | undefined,
  grupsTenantId: string | undefined
): string | null => {
  if (!formTenantId || formTenantId === ALL_RULES_TENANTS_FILTER_ID) {
    return null;
  }

  if (formTenantId === GRUPS_RULES_TENANT_ID) {
    return grupsTenantId ?? null;
  }

  return formTenantId;
};

export const toFormFeaturedBadgeTenantId = (
  apiTenantId: string | null | undefined,
  grupsTenantId: string | undefined
): string => {
  if (apiTenantId == null) {
    return ALL_RULES_TENANTS_FILTER_ID;
  }

  if (grupsTenantId && apiTenantId === grupsTenantId) {
    return GRUPS_RULES_TENANT_ID;
  }

  return apiTenantId;
};

export const matchesFeaturedBadgeTenantFilter = (
  rowTenantId: string | null | undefined,
  filterTenantId: string,
  grupsTenantId: string | undefined
): boolean => {
  if (filterTenantId === GRUPS_RULES_TENANT_ID) {
    return rowTenantId === grupsTenantId;
  }

  return rowTenantId === filterTenantId;
};

export const normalizeFeaturedBadgeFormScope = (
  formValues: Pick<FeaturedBadgeRuleFormValues, "tenantId" | "eventTypeId" | "eventSubTypeId">,
  grupsTenantId?: string
): FeaturedBadgeRuleGroupDeletePayload => {
  const eventTypeId = formValues.eventTypeId ?? null;

  return {
    tenantId: toApiFeaturedBadgeTenantId(formValues.tenantId, grupsTenantId),
    eventTypeId,
    eventSubTypeId: eventTypeId == null ? null : (formValues.eventSubTypeId ?? null),
  };
};

const createEntryKey = (): string =>
  `entry-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export const getFeaturedBadgeStateIndex = (slug: string | undefined): number => {
  if (!slug) {
    return -1;
  }

  return FEATURED_BADGE_STATES.findIndex((state) => state.value === slug);
};

export const getMoreUrgentBadgeSlugs = (slug: string): string[] => {
  const selectedIndex = getFeaturedBadgeStateIndex(slug);
  if (selectedIndex <= 0) {
    return [];
  }

  return FEATURED_BADGE_STATES.slice(0, selectedIndex).map((state) => state.value);
};

export const getFeaturedBadgeDefaultLabel = (slug: string): string =>
  FEATURED_BADGE_STATES.find((state) => state.value === slug)?.label ?? "";

export const FEATURED_BADGE_DEFAULT_PRESETS: ReadonlyArray<{
  slug: string;
  label: string;
  thresholdMinutes: number;
}> = [
  { slug: "closed", label: "Inscripción cerrada", thresholdMinutes: 0 },
  { slug: "danger", label: "Últimos minutos", thresholdMinutes: 30 },
  { slug: "urgent", label: "¡Es hoy!", thresholdMinutes: 1440 },
  { slug: "soon", label: "Mañana", thresholdMinutes: 2880 },
  { slug: "warning", label: "Falta poco", thresholdMinutes: 4320 },
  { slug: "notice", label: "Se viene…", thresholdMinutes: 10080 },
];

export const createFeaturedBadgeEntry = (
  preset?: Partial<
    Pick<FeaturedBadgeRuleEntryValues, "slug" | "label" | "colorVariant" | "thresholdMinutes">
  >
): FeaturedBadgeRuleEntryValues => ({
  key: createEntryKey(),
  label: preset?.label ?? "",
  slug: preset?.slug ?? "",
  thresholdMinutes: preset?.thresholdMinutes ?? 0,
  colorVariant: preset?.colorVariant ?? 1,
});

export const createDefaultFeaturedBadgeEntries = (): FeaturedBadgeRuleEntryValues[] =>
  FEATURED_BADGE_DEFAULT_PRESETS.map((preset) => createFeaturedBadgeEntry(preset));

export const formatFeaturedBadgeTenantLabel = (
  tenantId: string | null | undefined,
  tenantName: string | null | undefined,
  tenantNameById?: ReadonlyMap<string, string>
): string => {
  if (tenantId == null) {
    return FEATURED_BADGE_ALL_TENANTS_LABEL;
  }

  return resolveRulesTenantDisplayName(tenantId, tenantName, tenantNameById);
};

export const formatEventScopeLabel = (
  row: Pick<
    FeaturedBadgeRule,
    "eventTypeId" | "eventSubTypeId" | "eventTypeName" | "eventSubTypeName"
  >
): string => {
  if (row.eventTypeId == null) {
    return "General";
  }

  const typeName = row.eventTypeName ?? `Tipo ${row.eventTypeId}`;
  const subTypeName =
    row.eventSubTypeId != null
      ? row.eventSubTypeName ?? `Subtipo ${row.eventSubTypeId}`
      : "General";

  return `${typeName} / ${subTypeName}`;
};

export const matchesFeaturedBadgeTypeFilter = (
  rowEventTypeId: number | null,
  filterEventTypeId?: number
): boolean => {
  if (filterEventTypeId == null) {
    return true;
  }

  return rowEventTypeId == null || rowEventTypeId === filterEventTypeId;
};

export const matchesFeaturedBadgeSubTypeFilter = (
  rowEventSubTypeId: number | null | undefined,
  filterEventSubTypeId?: number | null
): boolean => {
  if (filterEventSubTypeId == null) {
    return true;
  }

  return rowEventSubTypeId == null || rowEventSubTypeId === filterEventSubTypeId;
};

export const getFeaturedBadgeRuleScopeKey = (
  rule: Pick<FeaturedBadgeRule, "tenantId" | "eventTypeId" | "eventSubTypeId">
): string => `${rule.tenantId ?? ""}|${rule.eventTypeId ?? ""}|${rule.eventSubTypeId ?? ""}`;

export const buildFeaturedBadgeGroupScopePayload = (
  scope: Pick<FeaturedBadgeRuleGroup, "tenantId" | "eventTypeId" | "eventSubTypeId">
): FeaturedBadgeRuleGroupDeletePayload => ({
  tenantId: scope.tenantId,
  eventTypeId: scope.eventTypeId,
  eventSubTypeId: scope.eventSubTypeId,
});

export const buildFeaturedBadgeGroupTogglePayload = (
  group: Pick<FeaturedBadgeRuleGroup, "tenantId" | "eventTypeId" | "eventSubTypeId" | "entries">,
  active: boolean
): FeaturedBadgeRuleGroupTogglePayload => ({
  ...buildFeaturedBadgeGroupScopePayload(group),
  active,
});

export const buildFeaturedBadgeGroupScopePayloadFromForm = (
  formValues: Pick<FeaturedBadgeRuleFormValues, "tenantId" | "eventTypeId" | "eventSubTypeId">,
  grupsTenantId?: string
): FeaturedBadgeRuleGroupDeletePayload =>
  normalizeFeaturedBadgeFormScope(formValues, grupsTenantId);

export const matchesFeaturedBadgeGroupScope = (
  row: FeaturedBadgeScope,
  scope: FeaturedBadgeScope
): boolean => isSameFeaturedBadgeScope(row, scope);

export const groupFeaturedBadgeRulesByScope = (
  rows: FeaturedBadgeRule[]
): FeaturedBadgeRuleGroup[] => {
  const groups = new Map<string, FeaturedBadgeRule[]>();

  for (const row of rows) {
    const key = getFeaturedBadgeRuleScopeKey(row);
    const bucket = groups.get(key) ?? [];
    bucket.push(row);
    groups.set(key, bucket);
  }

  return Array.from(groups.entries()).map(([groupKey, entries]) => {
    const sortedEntries = [...entries].sort(
      (left, right) => getFeaturedBadgeStateIndex(left.slug) - getFeaturedBadgeStateIndex(right.slug)
    );
    const first = sortedEntries[0];

    return {
      groupKey,
      id: first.id,
      tenantId: first.tenantId,
      tenantName: first.tenantName,
      eventTypeId: first.eventTypeId,
      eventSubTypeId: first.eventSubTypeId,
      eventTypeName: first.eventTypeName,
      eventSubTypeName: first.eventSubTypeName,
      entries: sortedEntries,
      active: sortedEntries.every((entry) => entry.active),
    };
  });
};

export const formatFeaturedBadgeEntriesSummary = (entries: FeaturedBadgeRule[]): string => {
  if (entries.length === 0) {
    return "-";
  }

  if (entries.length === 1) {
    return entries[0].label;
  }

  return `${entries.length} estados`;
};

export const sortFeaturedBadgeEntriesByUrgency = (
  entries: FeaturedBadgeRuleEntryValues[]
): FeaturedBadgeRuleEntryValues[] =>
  [...entries].sort((left, right) => {
    const leftIndex = getFeaturedBadgeStateIndex(left.slug);
    const rightIndex = getFeaturedBadgeStateIndex(right.slug);

    if (leftIndex === -1 && rightIndex === -1) {
      return 0;
    }
    if (leftIndex === -1) {
      return 1;
    }
    if (rightIndex === -1) {
      return -1;
    }

    return leftIndex - rightIndex;
  });

export interface EntryThresholdBounds {
  minMinutes: number | undefined;
  maxMinutes: number | undefined;
  previousLabel: string | undefined;
  nextLabel: string | undefined;
}

export const getEntryThresholdBounds = (
  entries: FeaturedBadgeRuleEntryValues[],
  targetIndex: number
): EntryThresholdBounds => {
  const sorted = sortFeaturedBadgeEntriesByUrgency(
    entries.filter((entry) => Boolean(entry.slug))
  );
  const targetEntry = entries[targetIndex];
  if (!targetEntry?.slug) {
    return { minMinutes: undefined, maxMinutes: undefined, previousLabel: undefined, nextLabel: undefined };
  }

  const sortedIndex = sorted.findIndex((entry) => entry.key === targetEntry.key);
  if (sortedIndex === -1) {
    return { minMinutes: undefined, maxMinutes: undefined, previousLabel: undefined, nextLabel: undefined };
  }

  const previousEntry = sortedIndex > 0 ? sorted[sortedIndex - 1] : undefined;
  const nextEntry = sortedIndex < sorted.length - 1 ? sorted[sortedIndex + 1] : undefined;

  const minMinutes = previousEntry?.thresholdMinutes != null
    ? previousEntry.thresholdMinutes
    : undefined;
  const maxMinutes = nextEntry?.thresholdMinutes != null && nextEntry.thresholdMinutes > 0
    ? nextEntry.thresholdMinutes
    : undefined;

  const previousLabel = previousEntry?.slug
    ? getFeaturedBadgeDefaultLabel(previousEntry.slug)
    : undefined;
  const nextLabel = nextEntry?.slug
    ? getFeaturedBadgeDefaultLabel(nextEntry.slug)
    : undefined;

  return { minMinutes, maxMinutes, previousLabel, nextLabel };
};

export const getThresholdBoundsHint = (bounds: EntryThresholdBounds): string | null => {
  const parts: string[] = [];

  if (bounds.minMinutes != null && bounds.minMinutes > 0 && bounds.previousLabel) {
    parts.push(`Mín: ${formatThresholdDuration(bounds.minMinutes)} (${bounds.previousLabel})`);
  }

  if (bounds.maxMinutes != null && bounds.nextLabel) {
    parts.push(`Máx: ${formatThresholdDuration(bounds.maxMinutes)} (${bounds.nextLabel})`);
  }

  return parts.length > 0 ? parts.join("  ·  ") : null;
};


export const ensureRequiredMoreUrgentEntries = (
  entries: FeaturedBadgeRuleEntryValues[]
): FeaturedBadgeRuleEntryValues[] => {
  const existingSlugs = new Set(
    entries.map((entry) => entry.slug).filter((slug): slug is string => Boolean(slug))
  );
  const requiredSlugs = new Set<string>();

  for (const entry of entries) {
    if (!entry.slug) {
      continue;
    }

    for (const slug of getMoreUrgentBadgeSlugs(entry.slug)) {
      requiredSlugs.add(slug);
    }
  }

  const missingEntries = Array.from(requiredSlugs)
    .filter((slug) => !existingSlugs.has(slug))
    .map((slug) => createFeaturedBadgeEntry({ slug }));

  if (missingEntries.length === 0) {
    return sortFeaturedBadgeEntriesByUrgency(entries);
  }

  return sortFeaturedBadgeEntriesByUrgency([...entries, ...missingEntries]);
};

export const canRemoveFeaturedBadgeEntry = (
  entries: FeaturedBadgeRuleEntryValues[],
  indexToRemove: number
): boolean => {
  if (entries.length <= 1) {
    return false;
  }

  const entryToRemove = entries[indexToRemove];
  if (!entryToRemove?.slug) {
    return true;
  }

  const removeIndex = getFeaturedBadgeStateIndex(entryToRemove.slug);
  if (removeIndex === -1) {
    return true;
  }

  const otherIndexes = entries
    .filter((_, index) => index !== indexToRemove)
    .map((entry) => getFeaturedBadgeStateIndex(entry.slug ?? ""))
    .filter((index) => index >= 0);

  if (otherIndexes.length === 0) {
    return true;
  }

  return removeIndex > Math.max(...otherIndexes);
};

export const getFeaturedBadgeEntryRemovalBlockReason = (
  entries: FeaturedBadgeRuleEntryValues[],
  indexToRemove: number
): string | null => {
  if (entries.length <= 1) {
    return "La regla debe tener al menos una etiqueta.";
  }

  if (!canRemoveFeaturedBadgeEntry(entries, indexToRemove)) {
    return "Solo podés quitar el estado menos urgente de la regla.";
  }

  return null;
};

export const getNextAvailableFeaturedBadgeSlug = (
  entries: FeaturedBadgeRuleEntryValues[]
): string | null => {
  const configuredIndexes = entries
    .map((entry) => getFeaturedBadgeStateIndex(entry.slug ?? ""))
    .filter((index) => index >= 0);

  const nextIndex =
    configuredIndexes.length === 0 ? 0 : Math.max(...configuredIndexes) + 1;

  if (nextIndex >= FEATURED_BADGE_STATES.length) {
    return null;
  }

  return FEATURED_BADGE_STATES[nextIndex]?.value ?? null;
};

export const validateFeaturedBadgeEntryContiguity = (
  entries: FeaturedBadgeRuleEntryValues[]
): string | null => {
  const indexes = entries
    .map((entry) => getFeaturedBadgeStateIndex(entry.slug ?? ""))
    .filter((index) => index >= 0)
    .sort((left, right) => left - right);

  if (indexes.length === 0) {
    return null;
  }

  if (indexes[0] !== 0) {
    return "La regla debe incluir el estado Cerrado.";
  }

  for (let index = 1; index < indexes.length; index += 1) {
    if (indexes[index] !== indexes[index - 1] + 1) {
      return "Los estados deben ser consecutivos desde Cerrado: no podés saltear estados intermedios.";
    }
  }

  return null;
};

const hasDuplicateRuleConfig = (
  rows: FeaturedBadgeRule[],
  tenantId: string | null,
  eventTypeId: number | null,
  eventSubTypeId: number | null,
  editingRowIds: number[]
): FeaturedBadgeRule | undefined => {
  const scope: FeaturedBadgeRuleGroupDeletePayload = {
    tenantId,
    eventTypeId,
    eventSubTypeId,
  };

  return rows.find(
    (row) => !editingRowIds.includes(row.id) && matchesFeaturedBadgeGroupScope(row, scope)
  );
};

export const formatFeaturedBadgeLabelMaxLengthError = (suffix = ""): string =>
  `El máximo de caracteres es ${FEATURED_BADGE_LABEL_MAX_LENGTH}${suffix}.`;

export const getEntryLabelLengthMessage = (
  label: string | undefined,
  suffix = ""
): string | null => {
  if ((label?.length ?? 0) > FEATURED_BADGE_LABEL_MAX_LENGTH) {
    return formatFeaturedBadgeLabelMaxLengthError(suffix);
  }
  return null;
};

export const getEntrySlugConflictMessage = (
  values: FeaturedBadgeRuleFormValues,
  entryIndex: number
): string | null => {
  const entry = values.entries[entryIndex];
  if (!entry || !entry.slug) {
    return null;
  }

  for (let otherIndex = 0; otherIndex < values.entries.length; otherIndex += 1) {
    if (otherIndex === entryIndex) {
      continue;
    }

    const otherEntry = values.entries[otherIndex];
    if (otherEntry.slug === entry.slug) {
      return values.entries.length > 1
        ? `El estado "${entry.slug}" ya está seleccionado en la etiqueta #${otherIndex + 1}. No se puede repetir un estado en la misma regla.`
        : "Este estado ya fue seleccionado en otra etiqueta del formulario.";
    }
  }

  return null;
};

export const findFeaturedBadgeRuleConflict = (
  rows: FeaturedBadgeRule[],
  values: FeaturedBadgeRuleFormValues,
  editingRowIds: number[],
  grupsTenantId?: string
): string | null => {
  const scope = normalizeFeaturedBadgeFormScope(values, grupsTenantId);
  const duplicateRule = hasDuplicateRuleConfig(
    rows,
    scope.tenantId,
    scope.eventTypeId,
    scope.eventSubTypeId,
    editingRowIds
  );

  if (duplicateRule) {
    const tenantLabel = formatFeaturedBadgeTenantLabel(
      duplicateRule.tenantId,
      duplicateRule.tenantName
    );
    const scopeLabel = formatEventScopeLabel(duplicateRule);

    return `Ya existe una regla configurada para Tenant "${tenantLabel}", Tipo/Subtipo "${scopeLabel}". Editá la regla existente en lugar de crear una nueva.`;
  }

  // Check for internal slug conflicts within the form
  for (let index = 0; index < values.entries.length; index += 1) {
    const message = getEntrySlugConflictMessage(values, index);
    if (message) {
      return message;
    }
  }

  return null;
};

export const isFeaturedBadgeClosedSlug = (slug: string | undefined): boolean => slug === "closed";

export const resolveFeaturedBadgeThresholdMinutes = (
  slug: string | undefined,
  thresholdMinutes: number | undefined
): number => (isFeaturedBadgeClosedSlug(slug) ? 0 : Number(thresholdMinutes ?? 0));

export const buildFeaturedBadgeCreatePayload = (
  entry: FeaturedBadgeRuleEntryValues,
  formValues: FeaturedBadgeRuleFormValues,
  grupsTenantId?: string
): FeaturedBadgeRuleCreatePayload => {
  const scope = normalizeFeaturedBadgeFormScope(formValues, grupsTenantId);

  return {
    tenantId: scope.tenantId,
    eventTypeId: scope.eventTypeId,
    eventSubTypeId: scope.eventSubTypeId,
    slug: String(entry.slug ?? "").trim(),
    label: String(entry.label ?? "").trim(),
    thresholdMinutes: resolveFeaturedBadgeThresholdMinutes(entry.slug, entry.thresholdMinutes),
    colorVariant: Number(entry.colorVariant ?? 1),
    active: Boolean(formValues.active),
  };
};

export const buildFeaturedBadgeUpdatePayload = (
  entry: FeaturedBadgeRuleEntryValues,
  formValues: FeaturedBadgeRuleFormValues,
  rowId: number,
  grupsTenantId?: string
): FeaturedBadgeRuleUpdatePayload => ({
  id: rowId,
  ...buildFeaturedBadgeCreatePayload(entry, formValues, grupsTenantId),
});

const sameNullableValue = <T,>(left: T | null | undefined, right: T | null | undefined): boolean =>
  (left ?? null) === (right ?? null);

export const isFeaturedBadgeEntryDirty = (
  entry: FeaturedBadgeRuleEntryValues,
  formValues: FeaturedBadgeRuleFormValues,
  originalRow: FeaturedBadgeRule | undefined,
  grupsTenantId?: string
): boolean => {
  if (!originalRow) {
    return true;
  }

  const payload = buildFeaturedBadgeCreatePayload(entry, formValues, grupsTenantId);

  return (
    originalRow.slug !== payload.slug ||
    originalRow.label !== payload.label ||
    originalRow.thresholdMinutes !== payload.thresholdMinutes ||
    originalRow.colorVariant !== payload.colorVariant ||
    originalRow.active !== payload.active ||
    !sameNullableValue(originalRow.tenantId, payload.tenantId) ||
    !sameNullableValue(originalRow.eventTypeId, payload.eventTypeId) ||
    !sameNullableValue(originalRow.eventSubTypeId, payload.eventSubTypeId)
  );
};

export const validateFeaturedBadgeForm = (values: FeaturedBadgeRuleFormValues): string | null => {
  if (!values.entries.length) {
    return "Agregá al menos una etiqueta.";
  }

  for (let index = 0; index < values.entries.length; index += 1) {
    const entry = values.entries[index];
    const suffix = values.entries.length > 1 ? ` #${index + 1}` : "";

    if (!entry.slug?.trim()) {
      return `Seleccioná el estado para la etiqueta${suffix}.`;
    }

    if (!entry.label?.trim()) {
      return `Ingresá el texto de la etiqueta${suffix}.`;
    }

    const labelLengthError = getEntryLabelLengthMessage(entry.label, suffix);
    if (labelLengthError) {
      return labelLengthError;
    }

    const thresholdMinutes = resolveFeaturedBadgeThresholdMinutes(entry.slug, entry.thresholdMinutes);

    if (!isFeaturedBadgeClosedSlug(entry.slug) && (Number.isNaN(thresholdMinutes) || thresholdMinutes < 0)) {
      return `Ingresá un valor válido en el umbral${suffix}.`;
    }
  }

  const sortedEntries = sortFeaturedBadgeEntriesByUrgency(
    values.entries.filter((entry) => Boolean(entry.slug))
  );
  for (let sortedIndex = 1; sortedIndex < sortedEntries.length; sortedIndex += 1) {
    const previousThreshold = Number(sortedEntries[sortedIndex - 1].thresholdMinutes ?? 0);
    const currentThreshold = Number(sortedEntries[sortedIndex].thresholdMinutes ?? 0);
    if (currentThreshold > 0 && previousThreshold > currentThreshold) {
      const previousLabel = getFeaturedBadgeDefaultLabel(sortedEntries[sortedIndex - 1].slug ?? "");
      const currentLabel = getFeaturedBadgeDefaultLabel(sortedEntries[sortedIndex].slug ?? "");
      return `El umbral de "${previousLabel}" no puede ser mayor al de "${currentLabel}", ya que es un estado más urgente.`;
    }
  }

  return validateFeaturedBadgeEntryContiguity(values.entries);
};

// Sin grupsTenantId resuelto, toApiFeaturedBadgeTenantId degrada el sentinel "grups"
// a null y la regla se guardaria como General, aplicando a todos los tenants.
export const isUnresolvedGrupsTenantSelection = (
  formTenantId: string | undefined,
  grupsTenantId: string | undefined
): boolean => formTenantId === GRUPS_RULES_TENANT_ID && !grupsTenantId;

export const UNRESOLVED_GRUPS_TENANT_ERROR =
  "No se pudo resolver el tenant Grups. Recargá la página e intentá de nuevo.";

export const validateFeaturedBadgeSubmission = (
  rows: FeaturedBadgeRule[],
  values: FeaturedBadgeRuleFormValues,
  editingRowIds: number[],
  grupsTenantId?: string
): string | null => {
  if (isUnresolvedGrupsTenantSelection(values.tenantId, grupsTenantId)) {
    return UNRESOLVED_GRUPS_TENANT_ERROR;
  }

  const fieldError = validateFeaturedBadgeForm(values);
  if (fieldError) {
    return fieldError;
  }

  return findFeaturedBadgeRuleConflict(rows, values, editingRowIds, grupsTenantId);
};
